import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, AlertTriangle, Calendar, ArrowRight, Mic } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, eur, dateFr, dateTimeFr } from "@/lib/api";
import StatutBadge from "@/components/StatutBadge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [entreprise, setEntreprise] = useState(null);
  const [stats, setStats] = useState(null);
  const [revenus, setRevenus] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.post("/relances/auto").catch(() => {});
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/entreprise").then((r) => {
      setEntreprise(r.data);
      if (r.data?.is_founder) {
        api.get("/revenus/plateforme").then((x) => setRevenus(x.data)).catch(() => {});
      }
    });
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="py-20 text-center text-stone-500">{t("commun.chargement")}</div>;

  const recents = [
    ...data.devis_recents.map((d) => ({ ...d, kind: "devis" })),
    ...data.factures_recentes.map((f) => ({ ...f, kind: "facture" })),
  ].sort((a, b) => (b.cree_le || "").localeCompare(a.cree_le || "")).slice(0, 8);

  return (
    <div data-testid="dashboard" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-serif font-bold text-2xl sm:text-3xl text-[#221F1A]">
            {t("dash.bonjour", { prenom: user?.nom?.split(" ")[0] })}
          </h1>
          <p className="text-stone-500 text-sm mt-1">{entreprise?.raison_sociale}</p>
        </div>
        <Link to="/devis/nouveau" className="btn-accent" data-testid="btn-nouveau-devis">
          <Plus size={20} /> {t("dash.nouveau_devis")}
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#12100D] rounded-xl p-5 border border-[#D9A461]/30" data-testid="stat-ca-encaisse">
          <div className="text-stone-400 text-sm font-medium">{t("dash.encaisse")}</div>
          <div className="amount-display mt-2">{eur(data.ca_encaisse)}</div>
        </div>
        <div className="panel !p-5" data-testid="stat-ca-en-attente">
          <div className="text-[#8A5A0F] text-sm font-medium">{t("dash.en_attente")}</div>
          <div className="font-serif font-bold text-2xl sm:text-3xl text-[#8A5A0F] mt-2">{eur(data.en_attente)}</div>
        </div>
        <div className="panel !p-5" data-testid="stat-ca-en-retard">
          <div className="text-[#A32E20] text-sm font-medium">{t("dash.en_retard")}</div>
          <div className="font-serif font-bold text-2xl sm:text-3xl text-[#A32E20] mt-2">{eur(data.en_retard)}</div>
        </div>
      </div>

      {entreprise?.is_founder && revenus && (
        <div className="bg-[#12100D] rounded-xl p-5 border border-[#D9A461]/30" data-testid="bloc-revenus-abonnement">
          <h2 className="font-serif font-semibold text-lg text-[#D9A461] mb-4">{t("dash.revenus_titre")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div data-testid="revenus-abonnes">
              <div className="text-stone-400 text-xs font-medium">{t("dash.revenus_abonnes")}</div>
              <div className="font-serif font-bold text-2xl text-white mt-1">{revenus.abonnes_actifs}</div>
            </div>
            <div data-testid="revenus-essais">
              <div className="text-stone-400 text-xs font-medium">{t("dash.revenus_essais")}</div>
              <div className="font-serif font-bold text-2xl text-white mt-1">{revenus.essais_en_cours}</div>
            </div>
            <div data-testid="revenus-mrr">
              <div className="text-stone-400 text-xs font-medium">{t("dash.revenus_mrr")}</div>
              <div className="font-serif font-bold text-2xl text-[#D9A461] mt-1">{eur(revenus.mrr_ht)}</div>
            </div>
            <div data-testid="revenus-total">
              <div className="text-stone-400 text-xs font-medium">{t("dash.revenus_total")}</div>
              <div className="font-serif font-bold text-2xl text-[#7BC496] mt-1">{eur(revenus.total_encaisse_ttc)}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
        <div className="panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif font-semibold text-lg">{t("dash.recents")}</h2>
            <Link to="/devis" className="text-[#8A5A2B] text-sm font-semibold flex items-center gap-1" data-testid="link-voir-tout">
              {t("commun.tout_voir")} <ArrowRight size={16} />
            </Link>
          </div>
          {recents.length === 0 ? (
            <p className="text-stone-500 text-sm py-6 text-center">{t("dash.vide")}</p>
          ) : (
            <div className="divide-y divide-[#E6DFD1]" data-testid="table-devis-factures">
              {recents.map((d) => (
                <button key={d.id} onClick={() => navigate(d.kind === "devis" ? `/devis/${d.id}` : `/factures/${d.id}`)}
                        data-testid={`row-${d.kind}-${d.id}`}
                        className="w-full flex items-center justify-between gap-3 py-3 hover:bg-[#FDFBF7] px-2 rounded-lg text-left">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">
                      {d.kind === "devis" ? t("dash.devis") : t("dash.facture")} {d.numero}
                    </div>
                    <div className="text-xs text-stone-500 truncate">{d.client_nom} · {dateFr(d.cree_le)}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-serif font-semibold">{eur(d.total_ttc ?? d.montant_ttc)}</span>
                    <StatutBadge statut={d.statut} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="panel" data-testid="bloc-cr-recents">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif font-semibold text-lg flex items-center gap-2">
              <Mic size={18} className="text-[#8A5A2B]" /> {t("dash.recents_cr")}
            </h2>
            <Link to="/comptes-rendus" className="text-[#8A5A2B] text-sm font-semibold flex items-center gap-1"
                  data-testid="link-voir-cr">
              {t("commun.tout_voir")} <ArrowRight size={16} />
            </Link>
          </div>
          {(data.comptes_rendus_recents || []).length === 0 ? (
            <p className="text-stone-500 text-sm py-6 text-center">{t("dash.vide_cr")}</p>
          ) : (
            <div className="divide-y divide-[#E6DFD1]" data-testid="liste-cr-recents">
              {data.comptes_rendus_recents.map((cr) => (
                <button key={cr.id} onClick={() => navigate(`/comptes-rendus?ouvrir=${cr.id}`)}
                        data-testid={`row-cr-${cr.id}`}
                        className="w-full flex items-center justify-between gap-3 py-3 hover:bg-[#FDFBF7] px-2 rounded-lg text-left">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{cr.titre}</div>
                    <div className="text-xs text-stone-500 truncate">
                      {cr.client_nom ? `${cr.client_nom} · ` : ""}{dateFr(cr.cree_le)}
                    </div>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border shrink-0 ${
                    cr.statut === "termine" ? "bg-[#E4F2E7] text-[#276440] border-[#B8E2C2]"
                    : cr.statut === "erreur" ? "bg-[#FBE4E0] text-[#A32E20] border-[#F2C2BA]"
                    : "bg-[#FCEFD9] text-[#8A5A0F] border-[#F6D9A9]"}`}
                        data-testid={`statut-cr-dash-${cr.id}`}>
                    {cr.statut === "termine" ? t("cr.statut_pret") : cr.statut === "erreur" ? t("cr.statut_erreur") : t("cr.statut_cours")}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        </div>

        <div className="space-y-4">
          <div className="panel" data-testid="widget-stock-bas">
            <h2 className="font-serif font-semibold text-lg mb-3 flex items-center gap-2">
              <AlertTriangle size={18} className="text-[#A32E20]" /> {t("dash.stock_bas")}
            </h2>
            {data.stock_bas.length === 0 ? (
              <p className="text-stone-500 text-sm">{t("dash.stock_ok")}</p>
            ) : (
              <div className="space-y-2">
                {data.stock_bas.slice(0, 5).map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-sm">
                    <span className="truncate">{a.nom}</span>
                    <span className="font-bold text-[#A32E20] shrink-0 ml-2">{a.quantite}</span>
                  </div>
                ))}
                <Link to="/stocks" className="text-[#8A5A2B] text-sm font-semibold inline-block mt-1" data-testid="link-stock">
                  {t("dash.gerer_stock")}
                </Link>
              </div>
            )}
          </div>

          <div className="panel" data-testid="widget-prochains-rdvs">
            <h2 className="font-serif font-semibold text-lg mb-3 flex items-center gap-2">
              <Calendar size={18} className="text-[#8A5A2B]" /> {t("dash.rdv")}
            </h2>
            {data.prochains_rdvs.length === 0 ? (
              <p className="text-stone-500 text-sm">{t("dash.aucun_rdv")}</p>
            ) : (
              <div className="space-y-3">
                {data.prochains_rdvs.map((r) => (
                  <div key={r.id} className="text-sm">
                    <div className="font-semibold">{r.titre}</div>
                    <div className="text-stone-500 text-xs">{r.client_nom} · {dateTimeFr(r.date_debut)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {stats && (stats.nb_devis > 0 || stats.nb_factures > 0) && (
        <div className="space-y-4" data-testid="section-statistiques">
          <h2 className="font-serif font-semibold text-xl">{t("dash.stats")}</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="panel !p-5" data-testid="stat-panier-moyen-devis">
              <div className="text-stone-500 text-sm font-medium">{t("dash.panier_devis")}</div>
              <div className="font-serif font-bold text-2xl sm:text-3xl text-[#221F1A] mt-2">{eur(stats.panier_devis)}</div>
              <div className="text-xs text-stone-500 mt-1">{t("dash.panier_devis_note", { count: stats.nb_devis })}</div>
            </div>
            <div className="panel !p-5" data-testid="stat-panier-moyen-facture">
              <div className="text-stone-500 text-sm font-medium">{t("dash.panier_facture")}</div>
              <div className="font-serif font-bold text-2xl sm:text-3xl text-[#221F1A] mt-2">{eur(stats.panier_factures)}</div>
              <div className="text-xs text-stone-500 mt-1">{t("dash.panier_facture_note", { count: stats.nb_factures })}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="panel" data-testid="graph-ca-mois">
              <h3 className="font-serif font-semibold text-lg mb-4">{t("dash.ca_mois")}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.ca_par_mois} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <XAxis dataKey="mois" tick={{ fontSize: 12, fill: "#57534E" }} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <Tooltip formatter={(v) => [eur(v), t("dash.ca_facture")]} cursor={{ fill: "#F7F4EE" }} />
                  <Bar dataKey="ca" fill="#D9A461" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="panel" data-testid="top-prestations">
              <h3 className="font-serif font-semibold text-lg mb-4">{t("dash.top_prestations")}</h3>
              {stats.top_ca.length === 0 ? (
                <p className="text-stone-500 text-sm">{t("dash.top_vide")}</p>
              ) : (
                <div className="space-y-4">
                  {stats.top_ca.map((s, i) => (
                    <div key={i} data-testid={`top-prestation-${i}`}>
                      <div className="flex justify-between gap-2 text-sm">
                        <span className="font-medium truncate">{s.designation}</span>
                        <span className="font-serif font-semibold shrink-0">{eur(s.ca)}</span>
                      </div>
                      <div className="h-2 bg-[#F7F4EE] rounded-full mt-1.5 overflow-hidden">
                        <div className="h-2 bg-[#D9A461] rounded-full"
                             style={{ width: `${Math.max(4, (s.ca / (stats.top_ca[0].ca || 1)) * 100)}%` }} />
                      </div>
                      <div className="text-xs text-stone-500 mt-1">{t("dash.quantite_vendue", { q: s.quantite })}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
